import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Paper
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';
import styles from '../styles/Home.module.css';

function Home() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const trackSponsorClick = () => trackEvent('flr_sponsor_click', { event_category: 'engagement', event_label: 'discord_invite_home' });

  const handleCardClick = (path, tabIndex = null) => {
    if (path === '/create-card' && tabIndex !== null) {
      // Navigate to create card page with specific tab
      navigate(path, { state: { activeTab: tabIndex } });
    } else {
      navigate(path);
    }
  };

  const cards = [
    {
      title: 'Browse Race Cards',
      description: 'Browse and join active race cards from other players',
      image: '/images/homepage/race-card-homepage.jpg',
      path: '/race-cards',
      tabIndex: null
    },
    {
      title: 'Create a 2 Player Career Card',
      description: 'Find a teammate for your F1 career mode',
      image: '/images/homepage/career-homepage.jpg',
      path: '/create-card',
      tabIndex: 0
    },
    {
      title: 'Create an Unranked Lobby Card',
      description: 'Host casual racing lobbies in Unranked mode',
      image: '/images/homepage/unranked-lobby-homepage.jpg',
      path: '/create-card',
      tabIndex: 1
    },
    {
      title: 'Create a Reserve Race Card',
      description: 'Have a spot in your league race? Our drivers can help with that',
      image: '/images/homepage/reserve-homepage.jpg',
      path: '/create-card',
      tabIndex: 2
    },
    {
      title: 'Create a Commentary Card',
      description: 'Find commentators to cover your league race events',
      image: '/images/commentary/commentary.png',
      path: '/create-card',
      tabIndex: 3
    },
    {
      title: 'Create a League Startup Card',
      description: 'Advertise your new league and find drivers to join you on the grid',
      image: '/images/2-player-career/preview-image.png',
      path: '/create-card',
      tabIndex: 4
    }
  ];

  return (
    <Container className={styles.homeContainer}>
      <Paper elevation={4} sx={{ maxWidth: 700, mx: 'auto', mt: 2, mb: 3, p: { xs: 1.5, md: 2 }, background: '#fff', borderRadius: 3, boxShadow: '0 4px 24px rgba(25, 118, 210, 0.08)' }}>
        <Box sx={{ width: 60, height: 4, background: 'linear-gradient(90deg, #1976d2 60%, #42a5f5 100%)', borderRadius: 2, mx: 'auto', mb: 2 }} />
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          className={styles.welcomeText}
          sx={{
            fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 700,
            letterSpacing: '1px',
            mb: 1,
            textAlign: 'center',
            background: 'linear-gradient(90deg, #1976d2 60%, #42a5f5 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            textShadow: '0 2px 8px rgba(25, 118, 210, 0.10)',
          }}
        >
          Welcome to
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <img
            src="/images/race-with-me-logo-blue.png"
            alt="RaceWithMe - F1 Game Racing Community Logo"
            className={styles.logoImage}
            style={{ height: '80px', maxWidth: '100%', objectFit: 'contain' }}
          />
        </Box>
        <Typography 
          variant="subtitle1" 
          component="h2" 
          gutterBottom 
          className={styles.subtitleText}
          sx={{
            fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 600,
            fontSize: { xs: '1rem', md: '1.1rem' },
            textAlign: 'center',
            mb: 0,
            color: '#1976d2',
            textShadow: '0 2px 8px rgba(25, 118, 210, 0.10)',
            letterSpacing: '0.5px',
          }}
        >
          Connect with F1 Game drivers and find your perfect racing friends.
        </Typography>
      </Paper>
      <Box className={styles.cardsContainer}>
        {cards.map((card, index) => (
          <Box key={index} className={styles.cardWrapper}>
            <Card 
              className={styles.raceCard}
              onClick={() => handleCardClick(card.path, card.tabIndex)}
            >
              {card.image.includes('/images/commentary/') || card.image.includes('preview-image') ? (
                <Box sx={{ backgroundColor: '#000', height: { xs: 180, md: 246 }, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0, overflow: 'hidden' }}>
                  <CardMedia
                    component="img"
                    height="246"
                    image={card.image}
                    alt={`${card.title} - ${card.description} - F1 Game Racing Community`}
                    className={styles.cardImage}
                    sx={{
                      height: { xs: 180, md: 246 },
                      objectFit: 'contain',
                      width: 'calc(100% + 2px)',
                      marginLeft: '-1px',
                      display: 'block'
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ height: { xs: 180, md: 246 }, lineHeight: 0, overflow: 'hidden', backgroundColor: '#1976d2' }}>
                  <CardMedia
                    component="img"
                    height="246"
                    image={card.image}
                    alt={`${card.title} - ${card.description} - F1 Game Racing Community`}
                    className={styles.cardImage}
                    sx={{
                      height: { xs: 180, md: 246 },
                      objectFit: 'cover',
                      width: 'calc(100% + 2px)',
                      marginLeft: '-1px',
                      display: 'block',
                      '@media (max-width: 500px)': {
                        objectFit: 'fill'
                      }
                    }}
                  />
                </Box>
              )}
              <CardContent className={styles.cardContent}>
                <Typography 
                  gutterBottom 
                  variant="h6" 
                  component="h3"
                  className={styles.cardTitle}
                  sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, fontWeight: 700, lineHeight:{ xs: '1.2rem', md: '1.8rem' } }}
                >
                  {card.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  className={styles.cardDescription}
                  sx={{ fontSize: { xs: '1.05rem', md: '1.15rem' } }}
                >
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
      {!isAuthenticated() && (
        <Box className={styles.authMessage}>
          <Typography variant="body1" color="text.secondary" className={styles.authText}>
            Sign in to create and manage your race cards
          </Typography>
        </Box>
      )}
      
      {/* Sponsor Banner */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        width: '100%',
        my: { xs: 3, md: 4 }
      }}>
        <a 
          href="https://discord.gg/NTGCabTJkA" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', display: 'block' }}
          onClick={trackSponsorClick}
        >
          <img 
            src="/images/sponsor-banner.png" 
            alt="Join RaceWithMe Discord Community - F1 Game Racing Partners" 
            style={{ 
              width: '100%', 
              height: 'auto', 
              maxWidth: '550px',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease-in-out',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          />
        </a>
      </Box>
    </Container>
  );
}

export default Home; 